import {
    Decimal,
    PrelTimeGetResponse,
    Ulid,
} from "@kase/my-marathon-communication";
import { intervalToDuration } from "date-fns";
import { dateToApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    CreatePreReportRequest,
    PreTimeReport,
    UpdatePreReportRequest,
} from "./interfaces";
import { getReportStart } from "./utils";

const getKey = (year: number, month: number) => `${year}-${month}`;

export const makePreliminary = ({ connection, company }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<PreTimeReport>();

    const view = (report: PrelTimeGetResponse) => {
        const finalReport: PreTimeReport = {
            ...report,
            fee_code:
                report.fee_code && Ulid.ZERO.equals(report.fee_code)
                    ? undefined
                    : report.fee_code,
            lob:
                report.lob && Ulid.ZERO.equals(report.lob)
                    ? undefined
                    : report.lob,
            project:
                report.project && Ulid.ZERO.equals(report.project)
                    ? undefined
                    : report.project,
            _id: report.id,
            id: report.id.toString(),
        };

        return finalReport;
    };

    const {
        items: reports,
        loading,
        listen,
    } = createListController<
        PrelTimeGetResponse,
        PreTimeReport,
        { year: number; month: number }
    >({
        name: "preliminary",
        connection,
        view,
        getKey: ({ year, month }) => getKey(year, month),
        list: (con, { year, month }) =>
            con.time.preliminary.items({
                company: company.value,
                year,
                month,
            }),
        emit,
    });

    const create = async ({
        start,
        end,
        project,
        feeCode,
        feeCategory,
        comment,
        onlyDuration,
    }: CreatePreReportRequest) => {
        const con = connection.value;
        const lob =
            feeCategory && Ulid.ZERO.equals(feeCategory)
                ? undefined
                : feeCategory;
        const duration = intervalToDuration({ start, end });
        const hours =
            (duration.days ?? 0) * 24 +
            (duration.hours ?? 0) +
            (duration.minutes ?? 0) / 60 +
            (duration.seconds ?? 0) / 3600;

        return con.time.preliminary.create({
            company: company.value,
            project,
            description: comment,
            date: dateToApi(start),
            start: getReportStart(start, onlyDuration),
            hours: new Decimal(hours),
            fee_code: feeCode,
            lob,
        });
    };

    const update = async ({
        id,
        start,
        end,
        project,
        feeCode,
        feeCategory,
        comment,
        onlyDuration,
    }: UpdatePreReportRequest) => {
        const con = connection.value;
        const duration = intervalToDuration({ start, end });
        const hours =
            (duration.days ?? 0) * 24 +
            (duration.hours ?? 0) +
            (duration.minutes ?? 0) / 60 +
            (duration.seconds ?? 0) / 3600;

        con.time.preliminary.update({
            id,
            company: company.value,
            project: project ?? Ulid.ZERO,
            description: comment,
            start: getReportStart(start, onlyDuration),
            hours: new Decimal(hours),
            fee_code: feeCode ?? Ulid.ZERO,
            lob: feeCategory ?? Ulid.ZERO,
        });
    };

    const upgrade = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        await con.time.preliminary.upgrade({
            id,
            company: company.value,
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        await con.time.preliminary.delete({ id, company: company.value });
    };

    return {
        reports,
        listen,
        loading,
        create,
        update,
        upgrade,
        remove,
        on,
        off,
    };
};
